import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import styles from './BankVerifyDetails.module.scss';

const { DetailsCard, Box, SubTitle, Table, Badge, Button, TextField } = vp;

type TablePropsHandlers = {
  verificationInformation: () => vp.TableProps;
  recipientInformation: () => vp.TableProps;
};

type AccountDetailsProps = {
  verification: vp.VerificationRequestsList;
};

type FC = FunctionComponent<AccountDetailsProps>;

const AccountDetails: FC = (props: AccountDetailsProps) => {
  const { verification } = props;

  return (
    <Box flx="col">
      <Box mb="lg" mt="lg">
        <SubTitle
          subTitle={`Send Verification Request to ${
            `${verification.Details.Credit.FirstName} ${verification.Details.Credit.LastName}` || ''
          }`}
        />
      </Box>
      <Box flx="col">
        <DetailsCard
          content={
            <Box flx="col" gap="md">
              <SubTitle subTitle="Verification Information" fontSize="20px" fontWeight="500" />
              <Table {...getTableProps('verificationInformation')} />
              <hr className={styles.hr} />
              <SubTitle subTitle="Recipient Information" fontSize="20px" fontWeight="500" />
              <Table {...getTableProps('recipientInformation')} />
            </Box>
          }
        />
      </Box>
      <Box flx="col" mt="md" gap="sm">
        {verification.Status.Request === 'active' && verification.Status.Verification === 'unverified' && (
          <>
            <SubTitle subTitle="Bank Verify Link" fontSize="16px" fontWeight="700" />
            <Box gap="md" ai="center">
              <div className={styles.inputContainer}>
                <TextField
                  name="Link"
                  variant="background"
                  height="38px"
                  value={verification.Link}
                  onBlur={() => {}}
                  onChange={() => {}}
                  width="700px"
                  readOnly
                />
              </div>
              <Button
                id="copy-url"
                label="Copy URL"
                color="lined"
                noShadow
                onClick={() => {
                  void navigator.clipboard.writeText(verification.Link);
                }}
              />
            </Box>
          </>
        )}
        <p
          style={{
            fontFamily: 'Ubuntu',
            fontSize: '12px',
            fontWeight: '400',
            lineHeight: '18px',
            letterSpacing: '0em',
            textAlign: 'left',
            color: '#8E8E8E',
          }}
        >
          Contact added on {verification.LastModified}
        </p>
      </Box>
    </Box>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      verificationInformation: () => {
        const verificationInformation: vp.TableProps = {
          name: 'verification-information',
          variant: 'detail',
          columns: {
            requestStatus: { header: 'Request Status', align: 'left' },
            verificationStatus: { header: 'Verification Status', align: 'left' },
            verificationAmount: { header: 'Amount', align: 'left' },
          },
          rows: [
            {
              requestStatus: (
                <Badge
                  label={capitalizeFirstLetter(verification.Status.Request) || ''}
                  color={GetColorStatus(verification.Status.Request)}
                  hasIcon
                />
              ),
              verificationStatus: (
                <Badge
                  label={capitalizeFirstLetter(verification.Status.Verification) || ''}
                  color={GetColorStatus(verification.Status.Verification)}
                  hasIcon
                />
              ),
              verificationAmount: `${verification.Details.Bank.Currency} ${verification.Amount}`,
            },
          ],
        };
        return verificationInformation;
      },
      recipientInformation: () => {
        const recipientInformation: vp.TableProps = {
          name: 'recipient-information',
          variant: 'detail',
          columns: {
            accountHolderName: { header: 'Account Holder Name', align: 'left' },
            emailAddress: { header: 'Email Address', align: 'left' },
            linkedAccount: { header: 'Linked Account ', align: 'left' },
          },
          rows: [
            {
              accountHolderName: `${verification.Details.Credit.FirstName} ${verification.Details.Credit.LastName}`,
              emailAddress: verification.EmailAddress.Receiver,
              linkedAccount: (
                <Box ai="center">
                  {verification.Details.Bank.FinancialInstitutionId !== null &&
                    verification.Details.Bank.FinancialInstitutionId !== '' &&
                    vp.hasBankImage(verification.Details.Bank.FinancialInstitutionId) && (
                      <img
                        src={`https://embed-dev.vopay.com/images/logos-banks/${verification.Details.Bank.FinancialInstitutionId}.svg`}
                        height="150px"
                        width="100px"
                        alt="Bank Logo"
                      />
                    )}
                  <span>{getMaskedNumber(verification.Details.Bank.AccountNumber)}</span>
                </Box>
              ),
            },
          ],
        };
        return recipientInformation;
      },
    };

    return handlers[handler]();
  }
  function GetColorStatus(status: string): 'positive' | 'neutral' | 'negative' | 'darkgrey' | 'red' {
    if (status === 'active' || status === 'verified') {
      return 'positive';
    }
    if (status === 'unverified') {
      return 'neutral';
    }
    if (status === 'failed') {
      return 'negative';
    }
    if (status === 'expired') {
      return 'red';
    }
    return 'darkgrey';
  }
  function capitalizeFirstLetter(stringCapitalize: string) {
    return stringCapitalize.charAt(0).toUpperCase() + stringCapitalize.slice(1);
  }
  function getMaskedNumber(num: string): string | null {
    if (typeof num === 'string') {
      return num.slice(-7).replace(/\d(?=\d{4})/g, '*');
    }

    // Return null or handle the case when num is not a string
    return null;
  }
};

export default AccountDetails;
